import { DateTime } from 'luxon';

/** Product default — NY US. Used when no configured source has a valid IANA zone. */
export const DEFAULT_TIMEZONE = 'America/New_York';

let mainTimezone = DEFAULT_TIMEZONE;

/** Current main IANA zone. SettingsService publishes here so util helpers can read it. */
export function getMainTimezone(): string {
  return mainTimezone;
}

export function setMainTimezone(zone: string | null | undefined): void {
  mainTimezone = isValidTimezone(zone) ? zone.trim() : DEFAULT_TIMEZONE;
}

/** Short label for display, e.g. ET, CST, CDT. NY stays ET to match existing copy. */
export function timezoneLabel(zone: string = getMainTimezone()): string {
  if (zone === DEFAULT_TIMEZONE) {
    return 'ET';
  }
  const short = DateTime.now().setZone(zone).offsetNameShort;
  return short || 'ET';
}

export type TimezoneSource = 'browser' | 'currentUser' | 'region' | 'client' | 'default';

export interface TimezoneSources {
  browser?: string | null;
  currentUser?: string | null;
  region?: string | null;
  client?: string | null;
}

export interface ResolvedTimezone {
  timezone: string;
  source: TimezoneSource;
}

export interface ResolveTimezoneOptions {
  /** When true, browser beats every other source. Off until a UI asks for it. */
  useBrowser?: boolean;
}

/**
 * Main timezone. Facility is not in this list — it is only used when a caller
 * asks for a facility explicitly.
 *
 * currentUser > region > client > default NY.
 * Browser is first only when `useBrowser` is on.
 */
export const TIMEZONE_PRECEDENCE: Array<'currentUser' | 'region' | 'client'> = [
  'currentUser',
  'region',
  'client',
];

export function isValidTimezone(zone: string | null | undefined): zone is string {
  if (!zone || typeof zone !== 'string') {
    return false;
  }
  const trimmed = zone.trim();
  if (!trimmed) {
    return false;
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

export function readBrowserTimezone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(zone) ? zone : null;
  } catch {
    return null;
  }
}

export function resolveTimezone(
  sources: TimezoneSources,
  options: ResolveTimezoneOptions = {},
): ResolvedTimezone {
  if (options.useBrowser && isValidTimezone(sources.browser)) {
    return { timezone: sources.browser.trim(), source: 'browser' };
  }

  for (const source of TIMEZONE_PRECEDENCE) {
    const value = sources[source];
    if (isValidTimezone(value)) {
      return { timezone: value.trim(), source };
    }
  }
  return { timezone: DEFAULT_TIMEZONE, source: 'default' };
}

/**
 * Interpret a UTC instant (ISO / epoch / Date) in `zone`. Non-ISO strings
 * are parsed as wall-clock in that zone, matching parseToEastern.
 */
export function parseToZone(
  raw: string | number | Date | null | undefined,
  zone: string = DEFAULT_TIMEZONE,
): DateTime | null {
  if (raw == null || raw === '') {
    return null;
  }
  const resolvedZone = isValidTimezone(zone) ? zone.trim() : DEFAULT_TIMEZONE;

  if (typeof raw === 'number' || raw instanceof Date) {
    const dt = DateTime.fromJSDate(new Date(raw), { zone: resolvedZone });
    return dt.isValid ? dt : null;
  }

  const str = String(raw).trim();
  const iso = DateTime.fromISO(str, { zone: resolvedZone });
  if (iso.isValid) {
    return iso;
  }

  const cleaned = str.replace(/\(.*?\)/g, '').trim();
  const nativeDate = new Date(cleaned);
  if (isNaN(nativeDate.getTime())) {
    return null;
  }

  const dt = DateTime.fromObject(
    {
      year: nativeDate.getFullYear(),
      month: nativeDate.getMonth() + 1,
      day: nativeDate.getDate(),
      hour: nativeDate.getHours(),
      minute: nativeDate.getMinutes(),
      second: nativeDate.getSeconds(),
    },
    { zone: resolvedZone },
  );
  return dt.isValid ? dt : null;
}

export function formatInZone(
  raw: string | number | Date | null | undefined,
  format: string,
  zone: string = DEFAULT_TIMEZONE,
): string {
  const dt = parseToZone(raw, zone);
  return dt?.isValid ? dt.toFormat(format) : '';
}
